import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import ApiService from '../services/api';

const CurrencyContext = createContext({
  currencyCode: 'ZMW',
  setCurrencyCode: () => {},
  formatCurrency: (amount) => String(amount),
});

export const CurrencyProvider = ({ children }) => {
  const defaultCurrency = (process.env.REACT_APP_CURRENCY || 'ZMW').toUpperCase();
  const [currencyCode, setCurrencyCode] = useState(defaultCurrency);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const settings = await ApiService.getSystemSettings();
        if (mounted && settings?.payments?.currency) {
          setCurrencyCode(String(settings.payments.currency).toUpperCase());
        }
      } catch (e) {
        try {
          const raw = localStorage.getItem('adminSettings');
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.payments?.currency) setCurrencyCode(String(parsed.payments.currency).toUpperCase());
          }
        } catch (_) {}
      }
    };
    load();

    const handler = (ev) => {
      const newSettings = ev?.detail;
      if (newSettings?.payments?.currency) {
        setCurrencyCode(String(newSettings.payments.currency).toUpperCase());
      }
    };
    window.addEventListener('systemSettingsUpdated', handler);
    return () => {
      mounted = false;
      window.removeEventListener('systemSettingsUpdated', handler);
    };
  }, []);

  const formatCurrency = useMemo(() => {
    return (amount, options = {}) => {
      try {
        const formatter = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode,
          ...options,
        });
        return formatter.format(Number(amount || 0));
      } catch (_) {
        // Fallback simple format
        return `${currencyCode} ${Number(amount || 0).toLocaleString()}`;
      }
    };
  }, [currencyCode]);

  const value = { currencyCode, setCurrencyCode, formatCurrency };
  return (
    <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);